import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/price";
import CreateListingDialog from "@/components/community/CreateListingDialog";
import { Plus, Tag, MapPin, Search, ArrowLeft, Bell, BellRing } from "lucide-react";

const CANTONS = ["VD", "GE", "VS", "FR", "NE", "JU", "BE", "ZH", "LU", "SG", "TI", "BS", "BL", "AG", "GR"];
const OBJ_CATS = ["tents", "furniture", "costumes", "crafts", "instruments", "other"];

export default function ClassifiedListings() {
  const { t } = useLang();
  const { user } = useAuth();
  const [kind, setKind] = useState("");
  const [cat, setCat] = useState("");
  const [canton, setCanton] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);

  const { data: myAlerts = [], refetch: refetchAlerts } = useQuery({
    queryKey: ["listingAlerts"],
    queryFn: async () => (await api.get("/community/listing-alerts")).data,
    enabled: !!user,
  });
  const alertTarget = cat || "all";
  const alertOn = myAlerts.includes(alertTarget);
  const toggleAlert = async () => {
    if (!user) { toast.error(t("lst_alert_login")); return; }
    try {
      if (alertOn) { await api.delete(`/community/listing-alerts/${alertTarget}`); toast.success(t("lst_alert_removed")); }
      else { await api.post("/community/listing-alerts", { object_category: alertTarget }); toast.success(t("lst_alert_saved")); }
      refetchAlerts();
    } catch { toast.error(t("lst_form_error")); }
  };

  const { data, refetch } = useQuery({
    queryKey: ["listings", kind, cat, canton, status, q],
    queryFn: async () => (await api.get("/community/listings", { params: {
      kind: kind || undefined, object_category: cat || undefined,
      canton: canton || undefined, status: status || undefined, q: q || undefined, limit: 48,
    } })).data,
  });
  const listings = data?.listings || [];

  const chip = (active) => `px-3 py-1.5 rounded-full text-xs transition-all ${active ? "bg-amber-600 text-stone-950 font-semibold" : "bg-[#121418] text-stone-300 border border-[#272A34] hover:border-amber-500/40"}`;

  return (
    <>
      <Seo title={t("lst_title")} description={t("lst_subtitle")} canonicalPath="/community/annonces" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <Link to="/community" className="inline-flex items-center gap-1.5 text-sm text-amber-500 hover:text-amber-400 mb-4"><ArrowLeft className="w-4 h-4" /> {t("nav_group_community")}</Link>
        <div className="mb-6 border-b border-amber-900/30 pb-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> swiss-pagan.ch</div>
            <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("lst_title")}</h1>
            <p className="text-stone-400 mt-3 max-w-2xl">{t("lst_subtitle")}</p>
          </div>
          {user && <button data-testid="listing-alert-btn" onClick={toggleAlert} title={alertTarget === "all" ? t("lst_alert_all_hint") : t(`lst_cat_${alertTarget}`)} className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border transition-all ${alertOn ? "bg-amber-950/40 border-amber-700/60 text-amber-300 hover:border-amber-500" : "bg-[#121418] border-[#272A34] text-stone-300 hover:border-amber-500/40"}`}>{alertOn ? <><BellRing className="w-4 h-4" /> {t("lst_alert_on")}</> : <><Bell className="w-4 h-4" /> {t("lst_alert")}</>}</button>}
          {user && <button data-testid="listing-create-btn" onClick={() => setDialog(true)} className="shrink-0 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2.5 rounded-md"><Plus className="w-4 h-4" /> {t("lst_create")}</button>}
        </div>

        <div className="space-y-3 mb-6">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="listing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("lst_search_ph")} className="w-full bg-[#0B0C0E] border border-[#272A34] rounded-md pl-9 pr-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setKind("")} className={chip(!kind)} data-testid="listing-kind-all">{t("lst_kind_all")}</button>
            <button onClick={() => setKind("sell")} className={chip(kind === "sell")} data-testid="listing-kind-sell">{t("lst_kind_sell")}</button>
            <button onClick={() => setKind("search")} className={chip(kind === "search")} data-testid="listing-kind-search">{t("lst_kind_search")}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCat("")} className={chip(!cat)}>{t("lst_cat_all")}</button>
            {OBJ_CATS.map((c) => <button key={c} data-testid={`listing-cat-${c}`} onClick={() => setCat(c)} className={chip(cat === c)}>{t(`lst_cat_${c}`)}</button>)}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select value={canton} onChange={(e) => setCanton(e.target.value)} data-testid="listing-canton" className="bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-1.5 text-xs text-stone-300">
              <option value="">{t("lst_canton_all")}</option>
              {CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="listing-status" className="bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-1.5 text-xs text-stone-300">
              <option value="">{t("lst_status_all")}</option>
              <option value="available">{t("lst_status_available")}</option>
              <option value="sold">{t("lst_status_sold")}</option>
            </select>
          </div>
        </div>

        {listings.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((l) => (
              <Link key={l.id} to={`/community/annonces/${l.id}`} data-testid={`listing-card-${l.id}`} className="group bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-xl overflow-hidden transition-all hover:-translate-y-1">
                <div className="relative h-44 bg-[#0B0C0E]">
                  {l.photos?.[0] ? <img src={l.photos[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Tag className="w-8 h-8 text-stone-700" /></div>}
                  <span className={`absolute top-2 left-2 text-[10px] font-mono uppercase px-2 py-1 rounded ${l.kind === "search" ? "bg-indigo-600/90 text-white" : "bg-amber-600/90 text-stone-950"}`}>{l.kind === "search" ? t("lst_kind_search") : t("lst_kind_sell")}</span>
                  {l.pinned && <span className="absolute bottom-2 left-2 text-[10px] font-mono uppercase px-2 py-1 rounded bg-emerald-600/90 text-white inline-flex items-center gap-1" data-testid={`listing-pinned-${l.id}`}>★ {t("lst_pinned")}</span>}
                  {l.listing_status === "sold" && <span className="absolute top-2 right-2 text-[10px] font-mono uppercase px-2 py-1 rounded bg-stone-900/90 text-stone-300 border border-stone-600">{t("lst_status_sold")}</span>}
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-lg text-amber-100 group-hover:text-amber-50 leading-tight line-clamp-2">{l.title}</h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-serif text-amber-400">{l.kind === "sell" ? (l.price_cents != null ? formatPrice(l.price_cents, l.currency) : t("lst_none_price")) : t("lst_looking_for")}</span>
                    {l.canton && <span className="inline-flex items-center gap-1 text-xs text-stone-500"><MapPin className="w-3.5 h-3.5" /> {l.canton}</span>}
                  </div>
                  <div className="mt-1 text-[11px] font-mono text-stone-500">{t(`lst_cat_${l.object_category}`)}</div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-stone-500 border border-dashed border-[#272A34] rounded-lg" data-testid="listings-empty">{t("lst_empty")}</div>
        )}
      </div>
      <CreateListingDialog open={dialog} onClose={() => setDialog(false)} onCreated={() => { setDialog(false); refetch(); }} />
    </>
  );
}
